import { motion } from "framer-motion";
import { Gauge, ClipboardCheck, BadgePercent, Ruler } from "lucide-react";
import { fadeUp } from "./shared";

const whyUsItems = [
    {
        icon: Gauge,
        title: "Gyorsaság",
        text: "Rövid határidők, feszes ütemterv. Időben kezdünk, időben végzünk — a projekt minden fázisában.",
    },
    {
        icon: ClipboardCheck,
        title: "Teljeskörű ügyintézés",
        text: "Engedélyek, dokumentáció és ügyintézés — a teljes folyamatot átláthatóan, egy kézből kezeljük.",
    },
    {
        icon: BadgePercent,
        title: "Kedvező árak",
        text: "Transzparens, fix árajánlat. Nincs rejtett költség, nincs utólagos meglepetés.",
    },
    {
        icon: Ruler,
        title: "Precíz munkavégzés",
        text: "Mérnöki pontosság, prémium anyagok, minőségellenőrzés a projekt minden szakaszában.",
    },
];

export const WhyUs = () => (
    <section
        id="miert-minket"
        data-testid="why-us-section"
        className="relative bg-[#0A0A0A] text-white py-24 sm:py-32 lg:py-40 overflow-hidden"
    >
        {/* Subtle grid pattern background */}
        <div
            aria-hidden
            className="absolute inset-0 opacity-[0.05] pointer-events-none"
            style={{
                backgroundImage:
                    "linear-gradient(#7ED4CE 1px, transparent 1px), linear-gradient(90deg, #7ED4CE 1px, transparent 1px)",
                backgroundSize: "80px 80px",
            }}
        />
        {/* Soft teal glow */}
        <div
            aria-hidden
            className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none"
            style={{
                background:
                    "radial-gradient(closest-side, rgba(13,122,115,0.35), transparent 70%)",
            }}
        />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            {/* Editorial header */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 pb-16 sm:pb-24 border-b border-white/15">
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-100px" }}
                    className="lg:col-span-4 flex flex-col gap-4"
                >
                    <span className="font-mono-label text-xs text-[#7ED4CE] tabular">
                        (02) &nbsp; Filozófia
                    </span>
                    <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                        Miért a <br className="hidden sm:block" />
                        <span className="text-[#7ED4CE]">Stabilor?</span>
                    </h2>
                </motion.div>

                <motion.div
                    variants={fadeUp}
                    custom={1}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-100px" }}
                    className="lg:col-span-7 lg:col-start-6 flex items-end"
                >
                    <p className="text-white/75 text-lg sm:text-xl leading-relaxed max-w-2xl">
                        Négy alappillérre építkezünk — mindegyik ugyanolyan
                        fontos. Ezek határozzák meg, hogy egy Stabilor projekt
                        határidőre, tisztességes áron és kifogástalan minőségben
                        készül el.
                    </p>
                </motion.div>
            </div>

            {/* Numbered feature grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10 border-t-0">
                {whyUsItems.map((item, i) => {
                    const Icon = item.icon;
                    return (
                        <motion.div
                            key={item.title}
                            variants={fadeUp}
                            custom={i}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-80px" }}
                            data-testid={`why-card-${item.title}`}
                            className="group relative bg-[#0A0A0A] p-8 sm:p-12 flex flex-col gap-8 min-h-[280px]"
                        >
                            <div className="flex items-start justify-between">
                                <span className="font-mono-label text-xs text-white/40 tabular">
                                    0{i + 1} / 04
                                </span>
                                <div className="w-12 h-12 flex items-center justify-center bg-white/5 group-hover:bg-[#0D7A73] transition-colors duration-500">
                                    <Icon
                                        className="w-6 h-6 text-[#7ED4CE] group-hover:text-white transition-colors duration-500"
                                        strokeWidth={1.8}
                                    />
                                </div>
                            </div>

                            <div>
                                <h3 className="font-display font-bold text-3xl sm:text-4xl leading-tight">
                                    {item.title}
                                </h3>
                                <p className="mt-4 text-white/65 leading-relaxed text-base sm:text-lg max-w-md">
                                    {item.text}
                                </p>
                            </div>

                            <span className="absolute left-0 bottom-0 h-[2px] w-0 bg-[#7ED4CE] transition-[width] duration-700 group-hover:w-full" />
                        </motion.div>
                    );
                })}
            </div>
        </div>
    </section>
);
